// @ts-check
// Chip "Do painel" da lista aberta por um clique num gráfico (contrato 07, seção 10): de onde veio (painel, aba, widget
// e ponto), quantos registros, quando foi calculado e os avisos (lista diferente do número, pares em cards, número do
// ERP, taxa, base de uma média, colunas ocultas do kanban), com "Voltar ao painel", "Atualizar" (só no snapshot) e
// "Limpar". No celular vira "Do painel (42)" e o texto completo abre numa folha. Usado na lista e na caixa de conversas.
// A lista aberta por um contador de fluxo (receita com flowId, contrato 09 de fluxos) diz "Fluxo: <fluxo> > <passo> >
// <grupo>" e volta para o fluxo.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { describeDrilldown, resolveDrilldown } from '../../data/dashboards.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Button } from '../../ui/button.js';
import { EmptyState } from '../../ui/feedback.js';
import { getErrorMessage } from '../../data/api-client.js';
import { Icon } from '../../ui/icon.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatDate, formatDateTime, formatNumber, formatTime } from '../../lib/format.js';
import { dashboardHrefFromRecipe, drilldownChipNotes, drilldownModeOf, drilldownPeriodOf, recipeOf } from '../../lib/drilldown-links.js';
import { rememberDrilldownResult, rememberedDrilldown } from '../dashboards/drilldown.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { isFlowDrilldownRecipe } from '../../../../shared/dashboards/drilldown-recipe.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {{ text?: string, i18nKey?: string, params?: Record<string, string | number> } | null | undefined} PointLabel
 * @typedef {{ dashboardId?: string, dashboardName?: string, tabName?: string, widgetTitle?: string | null, point?: PointLabel, series?: string | null,
 *   periodScope?: 'CURRENT_STATE' | 'ALL_TIME' | 'RANGE', period?: { from?: string | null, to?: string | null } | null,
 *   flowId?: string, nodeType?: string }} DrilldownLabel
 *   flowId e nodeType: lista de um contador de fluxo (o passo sem nome é o tipo, traduzido aqui)
 */

/**
 * Para onde o chip volta sem a origem guardada: o painel do clique, ou o editor do fluxo do contador.
 * @param {import('../../lib/drilldown-links.js').DrilldownRecipe} recipe
 */
function originHrefOf(recipe) {
  if (isFlowDrilldownRecipe(recipe)) return `/campaigns/flows/${encodeURIComponent(String(/** @type {any} */ (recipe).flowId))}`;
  return dashboardHrefFromRecipe(recipe);
}

/** Sobe a cada "Atualizar": a lista e a caixa de conversas montam de novo com o snapshot recalculado. */
export const drilldownRefreshState = signal(0);

// prefixos onde o rótulo de uma série, coluna, bloco ou medida que vem como chave pode estar traduzido
const SERIES_TRANSLATION_PREFIXES = ['dashboardDrilldown.series.', 'geoMap.metrics.', 'reports.funnel.tabs.', 'reports.stageFlow.', 'reports.stats.'];

/** @param {PointLabel} point */
export function pointLabelText(point) {
  if (!point) return '';
  if (point.text) return point.text;
  if (point.i18nKey && hasTranslation(point.i18nKey)) return t(point.i18nKey, point.params ?? {});
  return '';
}

/**
 * Rótulo da série, coluna, bloco ou medida; uma chave de opção (situação da rematrícula) vira o rótulo da opção.
 * @param {string | null | undefined} series @param {string | null} [objectNameSingular]
 */
export function seriesLabelText(series, objectNameSingular = null) {
  if (!series) return '';
  const prefix = SERIES_TRANSLATION_PREFIXES.find((candidate) => hasTranslation(`${candidate}${series}`));
  if (prefix) return t(`${prefix}${series}`);
  const object = objectNameSingular ? objectsByNameSingular.value.get(objectNameSingular) : null;
  for (const field of object?.fields ?? []) {
    const option = /** @type {any} */ (field).options?.find((/** @type {any} */ candidate) => candidate.value === series);
    if (option) return translateMetadataLabel(option.label);
  }
  return series;
}

/**
 * Passo de um contador de fluxo sem nome dado: o tipo do passo, ou a entrada.
 * @param {DrilldownLabel} label
 */
function flowStepText(label) {
  if (!label.flowId || !label.nodeType) return null;
  if (label.nodeType === 'ENTRY') return t('flows.drilldown.entry');
  const key = `flows.nodes.${label.nodeType}.name`;
  return hasTranslation(key) ? t(key) : null;
}

/**
 * Caminho do ponto: "Painel de captação > Funil por etapa > Visita agendada" (a aba só quando o painel tem mais de uma).
 * @param {DrilldownLabel | null | undefined} label @param {string | null} [objectNameSingular]
 */
export function drilldownPathText(label, objectNameSingular = null) {
  if (!label) return '';
  const point = pointLabelText(label.point);
  const series = seriesLabelText(label.series, objectNameSingular);
  const pointText = point && series ? `${point} (${series})` : point || series;
  const widgetTitle = label.widgetTitle || flowStepText(label);
  return [label.dashboardName, label.tabName, widgetTitle, pointText && pointText !== widgetTitle ? pointText : null]
    .filter((part) => typeof part === 'string' && part !== '')
    .join(' > ');
}

/**
 * Período (ou "situação de agora"), filtros do painel e a hora do cálculo, para a dica do chip e a folha do celular.
 * @param {import('../../lib/drilldown-links.js').DrilldownRecipe | null} recipe @param {DrilldownLabel | null | undefined} label
 * @param {string | null | undefined} computedAt
 */
function contextLines(recipe, label, computedAt) {
  const lines = [];
  const filters = recipe?.filters ?? {};
  const period = drilldownPeriodOf(label, recipe);
  if (period.kind === 'CURRENT_STATE') lines.push(t('dashboardDrilldown.chip.currentState'));
  else if (period.kind === 'DAYS') lines.push(t('dashboardDrilldown.chip.period', { from: formatDate(period.fromDay), to: formatDate(period.toDay) }));
  else if (period.kind === 'INSTANTS') lines.push(t('dashboardDrilldown.chip.period', { from: formatDateTime(period.from), to: formatDateTime(period.to) }));
  else lines.push(t('dashboardDrilldown.chip.allTime'));
  if (filters.campusId || filters.ownerWorkspaceMemberId || filters.academicPeriodId) lines.push(t('dashboardDrilldown.chip.extraFilters'));
  if (computedAt) lines.push(t('dashboardDrilldown.chip.computedAt', { time: formatTime(computedAt) }));
  return lines;
}

/**
 * Texto de um aviso do chip, com os números no formato da pessoa.
 * @param {import('../../lib/drilldown-links.js').DrilldownChipNote} note
 */
function noteText(note) {
  const params = Object.fromEntries(Object.entries(note.params).map(([key, value]) => [key, key === 'count' ? value : formatNumber(value)]));
  return t(`dashboardDrilldown.chip.${note.key}`, params);
}

/**
 * @param {{ parameter: string, totalCount?: number | null, viewType?: string | null, hiddenCount?: number | null, onClear: () => void }} props
 *   parameter: `f.<receita>` ou `s.<receita>` da URL; totalCount: total da lista aberta (null enquanto carrega)
 */
export function DrilldownChip({ parameter, totalCount = null, viewType = null, hiddenCount = null, onClear }) {
  const isMobile = useIsMobile();
  const remembered = rememberedDrilldown(parameter);
  const [info, setInfo] = useState(/** @type {any} */ (remembered?.data ?? null));
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const recipe = recipeOf(parameter);
  const mode = drilldownModeOf(parameter);

  useEffect(() => {
    const cached = rememberedDrilldown(parameter)?.data;
    setIsUnavailable(false);
    if (cached) {
      setInfo(cached);
      return undefined;
    }
    setInfo(null);
    let isCancelled = false;
    describeDrilldown(parameter)
      .then((data) => {
        if (!isCancelled) setInfo(data);
      })
      .catch(() => {
        // sem DASHBOARDS (ou painel apagado) a lista continua valendo; só o rótulo não carrega
        if (!isCancelled) setIsUnavailable(true);
      });
    return () => {
      isCancelled = true;
    };
  }, [parameter]);

  const path = drilldownPathText(info?.label, info?.objectNameSingular ?? null);
  const count = typeof info?.count === 'number' ? info.count : totalCount;
  const notes = drilldownChipNotes(info, { listCount: totalCount, hiddenCount, viewType, shownValue: remembered?.shownValue ?? null });
  const isWarning = notes.some((note) => note.key === 'mismatch' || note.key === 'staleValue');
  const lines = contextLines(recipe, info?.label, info?.computedAt);
  const isFlow = Boolean(recipe && isFlowDrilldownRecipe(recipe));
  const labelText = isUnavailable || !path
    ? t(isFlow ? 'flows.drilldown.chipUnavailable' : 'dashboardDrilldown.chip.unavailable')
    : t(isFlow ? 'flows.drilldown.chipLabel' : 'dashboardDrilldown.chip.label', { path });
  const countText = typeof count === 'number' ? t('dashboardDrilldown.chip.count', { count }) : '';
  const dashboardHref = remembered?.origin ?? (recipe ? originHrefOf(recipe) : null);
  const chipIcon = isFlow ? 'IconSitemap' : 'IconChartBar';

  const refresh = async () => {
    if (!recipe || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const encoded = /** @type {string} */ (parameter.slice(2));
      const data = await resolveDrilldown(encoded);
      rememberDrilldownResult(data, remembered?.origin ?? null, remembered?.shownValue ?? null);
      setInfo(data);
      // a mesma lista: monta de novo com o snapshot recalculado; outro modo: troca o endereço
      if (data?.drilldownParam === parameter) drilldownRefreshState.value += 1;
      else if (typeof data?.url === 'string') navigate(data.url, { replace: true });
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const actions = html`
    ${dashboardHref
      ? html`<button type="button" class="drilldown-chip-action" onClick=${() => navigate(dashboardHref)}>
          <${Icon} name="IconArrowLeft" size=${14} /><span>${t(isFlow ? 'flows.drilldown.backToFlow' : 'dashboardDrilldown.chip.backToDashboard')}</span>
        </button>`
      : null}
    ${mode === 'SNAPSHOT' && recipe
      ? html`<button type="button" class="drilldown-chip-action" disabled=${isRefreshing} aria-busy=${isRefreshing ? 'true' : undefined} onClick=${refresh}>
          <${Icon} name="IconRefresh" size=${14} /><span>${t('dashboardDrilldown.chip.refresh')}</span>
        </button>`
      : null}`;
  const details = html`
    <ul class="drilldown-chip-lines">${lines.map((line) => html`<li key=${line}>${line}</li>`)}</ul>
    ${notes.length > 0 ? html`<ul class="drilldown-chip-notes">${notes.map((note) => html`<li key=${note.key} data-note=${note.key}>${noteText(note)}</li>`)}</ul>` : null}`;

  if (isMobile) {
    return html`<div class="drilldown-chip" data-compact="true" data-testid="drilldown-chip">
      <button ref=${anchorRef} type="button" class="drilldown-chip-main" aria-expanded=${isSheetOpen ? 'true' : 'false'} onClick=${() => setIsSheetOpen(!isSheetOpen)}>
        <${Icon} name=${chipIcon} size=${16} />
        <span class="drilldown-chip-label">${t(isFlow ? 'flows.drilldown.chipCompact' : 'dashboardDrilldown.chip.compact', { count: typeof count === 'number' ? formatNumber(count) : '…' })}</span>
        ${isWarning ? html`<${Icon} name="IconAlertTriangle" size=${14} />` : null}
      </button>
      <button type="button" class="drilldown-chip-remove" aria-label=${t('dashboardDrilldown.chip.clearAriaLabel')} onClick=${onClear}>
        <${Icon} name="IconX" size=${14} />
      </button>
      <${Dropdown} anchorRef=${anchorRef} isOpen=${isSheetOpen} onClose=${() => setIsSheetOpen(false)} width=${320} className="drilldown-chip-sheet">
        <div class="drilldown-chip-sheet-body">
          <p class="drilldown-chip-sheet-title">${labelText}</p>
          ${countText ? html`<p class="drilldown-chip-sheet-count">${countText}</p>` : null}
          ${details}
          <div class="drilldown-chip-sheet-actions">${actions}</div>
        </div>
      <//>
    </div>`;
  }

  return html`<div class="drilldown-chip-row">
    <div class="drilldown-chip" data-testid="drilldown-chip" title=${[labelText, countText, ...lines].filter(Boolean).join('\n')}>
      <span class="drilldown-chip-main">
        <${Icon} name=${chipIcon} size=${16} />
        <span class="drilldown-chip-label">${labelText}</span>
        ${countText ? html`<span class="drilldown-chip-count">· ${countText}</span>` : null}
        ${info?.computedAt ? html`<span class="drilldown-chip-time">· ${t('dashboardDrilldown.chip.computedAt', { time: formatTime(info.computedAt) })}</span>` : null}
      </span>
      <button type="button" class="drilldown-chip-remove" aria-label=${t('dashboardDrilldown.chip.clearAriaLabel')} title=${t('dashboardDrilldown.chip.clear')} onClick=${onClear}>
        <${Icon} name="IconX" size=${14} />
      </button>
    </div>
    ${actions}
    ${notes.length > 0
      ? html`<span class="drilldown-chip-note" data-tone=${isWarning ? 'warning' : undefined}>
          <${Icon} name=${isWarning ? 'IconAlertTriangle' : 'IconInfoCircle'} size=${14} />
          <span>${notes.map(noteText).join(' · ')}</span>
        </span>`
      : null}
  </div>`;
}

/**
 * Lista vazia de um recorte do painel: o ponto não tem registros agora (zerou desde o clique, link reaberto mais tarde ou
 * recebido de um colega) ou o recorte não abriu. No lugar do convite para criar o primeiro registro do objeto, diz isso
 * e oferece a volta ao painel e "Limpar".
 * @param {{ parameter: string, error?: unknown, onClear: () => void }} props
 */
export function DrilldownEmptyState({ parameter, error = null, onClear }) {
  const recipe = recipeOf(parameter);
  const dashboardHref = rememberedDrilldown(parameter)?.origin ?? (recipe ? originHrefOf(recipe) : null);
  const isFlow = Boolean(recipe && isFlowDrilldownRecipe(recipe));
  return html`<${EmptyState}
    icon="IconChartBar"
    title=${error ? t('dashboardDrilldown.empty.errorTitle') : t('dashboardDrilldown.empty.title')}
    subtitle=${error ? getErrorMessage(error) : t('dashboardDrilldown.empty.subtitle')}
    action=${html`<div class="drilldown-empty-actions">
      ${dashboardHref ? html`<${Button} variant="secondary" icon="IconArrowLeft" title=${t(isFlow ? 'flows.drilldown.backToFlow' : 'dashboardDrilldown.chip.backToDashboard')} onClick=${() => navigate(dashboardHref)} />` : null}
      <${Button} variant="secondary" icon="IconX" title=${t('dashboardDrilldown.chip.clear')} onClick=${onClear} />
    </div>`}
  />`;
}
